import React, { useState } from "react";
import {
  Card,
  Accordion,
  Row,
  Col,
  Table,
  Form,
  InputGroup,
} from "react-bootstrap";

const Recipes = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const recipes = [
    {
      name: "King Arthur Classic Sourdough",
      source: "King Arthur Baking Company",
      sourceUrl:
        "https://www.kingarthurbaking.com/recipes/classic-sourdough-bread-recipe",
      description:
        "A traditional sourdough bread with a mild tang and beautiful open crumb.",
      ingredients: {
        dough: [
          {
            item: "Ripe sourdough starter",
            amount: "200g",
            notes: "100% hydration, fed and active",
          },
          { item: "Water, lukewarm", amount: "280g", notes: "90-95°F" },
          {
            item: "Bread flour",
            amount: "500g",
            notes: "King Arthur bread flour recommended",
          },
          { item: "Salt", amount: "10g", notes: "2 teaspoons" },
        ],
      },
      hydration: "70%",
      timeline: [
        {
          step: "Feed Starter",
          duration: "4-12 hours",
          notes: "Until doubled and bubbly",
        },
        {
          step: "Mix Dough",
          duration: "15 minutes",
          notes: "Combine all ingredients except salt",
        },
        { step: "Autolyse", duration: "30 minutes", notes: "Rest dough" },
        {
          step: "Add Salt & Knead",
          duration: "5 minutes",
          notes: "Incorporate salt thoroughly",
        },
        {
          step: "Bulk Fermentation",
          duration: "4-6 hours",
          notes: "Perform 3-4 sets of stretch and folds",
        },
        {
          step: "Pre-shape",
          duration: "15 minutes",
          notes: "Light shaping, bench rest",
        },
        {
          step: "Final Shape",
          duration: "10 minutes",
          notes: "Create tension, place in banneton",
        },
        {
          step: "Cold Proof",
          duration: "8-16 hours",
          notes: "Refrigerate overnight",
        },
        {
          step: "Bake",
          duration: "45 minutes",
          notes: "450°F, with steam for first 20 minutes",
        },
      ],
    },
    {
      name: "Whole Wheat Sourdough",
      source: "Adapted from King Arthur",
      description:
        "A heartier version with 50% whole wheat flour for more nutrition and flavor.",
      ingredients: {
        dough: [
          {
            item: "Ripe sourdough starter",
            amount: "200g",
            notes: "100% hydration",
          },
          { item: "Water, lukewarm", amount: "300g", notes: "90-95°F" },
          { item: "Bread flour", amount: "250g", notes: "" },
          { item: "Whole wheat flour", amount: "250g", notes: "" },
          { item: "Salt", amount: "10g", notes: "2 teaspoons" },
        ],
      },
      hydration: "75%",
      timeline: [
        {
          step: "Feed Starter",
          duration: "4-12 hours",
          notes: "Until doubled and bubbly",
        },
        {
          step: "Mix Dough",
          duration: "15 minutes",
          notes: "Combine all ingredients",
        },
        {
          step: "Autolyse",
          duration: "45 minutes",
          notes: "Longer for whole wheat",
        },
        {
          step: "Bulk Fermentation",
          duration: "5-6 hours",
          notes: "4-5 sets of stretch and folds",
        },
        {
          step: "Shape & Proof",
          duration: "12-15 hours",
          notes: "Cold proof in refrigerator",
        },
        { step: "Bake", duration: "45 minutes", notes: "450°F, with steam" },
      ],
    },
    {
      name: "Gluten-Free Sourdough",
      source: "Adapted from Cultures for Health",
      description:
        "A gluten-free sourdough bread that's soft inside with a crispy crust.",
      ingredients: {
        starter: [
          {
            item: "Brown rice flour",
            amount: "100g",
            notes: "For feeding starter",
          },
          {
            item: "Water",
            amount: "100g",
            notes: "Filtered, room temperature",
          },
        ],
        dough: [
          {
            item: "GF sourdough starter",
            amount: "150g",
            notes: "100% hydration, very active",
          },
          { item: "Brown rice flour", amount: "200g", notes: "Finely ground" },
          { item: "Sorghum flour", amount: "100g", notes: "" },
          { item: "Tapioca starch", amount: "100g", notes: "" },
          {
            item: "Psyllium husk powder",
            amount: "20g",
            notes: "For structure",
          },
          { item: "Water, lukewarm", amount: "350g", notes: "90-95°F" },
          { item: "Salt", amount: "10g", notes: "2 teaspoons" },
          {
            item: "Honey",
            amount: "15g",
            notes: "Optional, helps fermentation",
          },
        ],
      },
      hydration: "80%",
      timeline: [
        {
          step: "Feed Starter",
          duration: "8-12 hours",
          notes: "GF starter may need more time",
        },
        {
          step: "Mix Dry Ingredients",
          duration: "5 minutes",
          notes: "Combine flours, psyllium, and salt",
        },
        {
          step: "Mix Wet Ingredients",
          duration: "5 minutes",
          notes: "Combine starter, water, and honey",
        },
        {
          step: "Combine & Mix",
          duration: "10 minutes",
          notes: "Mix until well combined",
        },
        {
          step: "First Rise",
          duration: "4-6 hours",
          notes: "Until visibly puffy, in a warm spot",
        },
        {
          step: "Shape",
          duration: "10 minutes",
          notes: "Shape gently and place in lined pan",
        },
        { step: "Second Rise", duration: "2-3 hours", notes: "Until doubled" },
        {
          step: "Bake",
          duration: "60 minutes",
          notes: "375°F, tent with foil if browning too quickly",
        },
      ],
    },
    {
      name: "Rye Sourdough",
      source: "Traditional European Recipe",
      description:
        "A dense, flavorful bread with a high percentage of rye flour.",
      ingredients: {
        dough: [
          {
            item: "Rye sourdough starter",
            amount: "200g",
            notes: "100% hydration",
          },
          { item: "Dark rye flour", amount: "400g", notes: "" },
          {
            item: "Bread flour",
            amount: "100g",
            notes: "For better structure",
          },
          { item: "Water, warm", amount: "350g", notes: "85-90°F" },
          { item: "Salt", amount: "12g", notes: "2.5 teaspoons" },
          { item: "Molasses", amount: "20g", notes: "Optional, for flavor" },
        ],
      },
      hydration: "78%",
      timeline: [
        {
          step: "Feed Starter",
          duration: "8-12 hours",
          notes: "Rye starter is very active",
        },
        { step: "Mix Dough", duration: "10 minutes", notes: "Will be sticky" },
        {
          step: "Bulk Fermentation",
          duration: "3-4 hours",
          notes: "Rye ferments faster",
        },
        { step: "Shape", duration: "5 minutes", notes: "Handle minimally" },
        { step: "Proof", duration: "2-3 hours", notes: "Until expanded" },
        { step: "Bake", duration: "50 minutes", notes: "425°F, with steam" },
      ],
    },
    {
      name: "High-Hydration Sourdough",
      source: "Advanced Baker's Recipe",
      description: "An advanced recipe for very open, holey crumb structure.",
      ingredients: {
        dough: [
          { item: "Mature starter", amount: "150g", notes: "100% hydration" },
          { item: "Water", amount: "400g", notes: "Reserve 50g for later" },
          {
            item: "Bread flour",
            amount: "500g",
            notes: "High protein content recommended",
          },
          { item: "Salt", amount: "10g", notes: "2 teaspoons" },
        ],
      },
      hydration: "85%",
      timeline: [
        {
          step: "Levain Build",
          duration: "8-12 hours",
          notes: "Until very active",
        },
        {
          step: "Initial Mix",
          duration: "10 minutes",
          notes: "Hold back 50g water",
        },
        {
          step: "Autolyse",
          duration: "2 hours",
          notes: "Longer autolyse for high hydration",
        },
        {
          step: "Add Salt & Water",
          duration: "15 minutes",
          notes: "Gradually add remaining water",
        },
        {
          step: "Bulk Fermentation",
          duration: "4-5 hours",
          notes: "6-8 sets of coil folds",
        },
        {
          step: "Pre-shape",
          duration: "20 minutes",
          notes: "Very gentle handling",
        },
        {
          step: "Final Shape",
          duration: "10 minutes",
          notes: "Create strong surface tension",
        },
        { step: "Cold Proof", duration: "14-16 hours", notes: "Refrigerate" },
        {
          step: "Bake",
          duration: "45 minutes",
          notes: "500°F, then 450°F, with steam",
        },
      ],
    },
    {
      name: "Jalapeño Cheddar Sourdough",
      source: "Original Recipe",
      description:
        "A spicy, cheesy twist on classic sourdough with fresh jalapeños and sharp cheddar.",
      ingredients: {
        dough: [
          {
            item: "Ripe sourdough starter",
            amount: "200g",
            notes: "100% hydration",
          },
          { item: "Water, lukewarm", amount: "300g", notes: "90-95°F" },
          {
            item: "Bread flour",
            amount: "500g",
            notes: "High protein content",
          },
          { item: "Salt", amount: "10g", notes: "2 teaspoons" },
        ],
        inclusions: [
          {
            item: "Sharp cheddar, cubed",
            amount: "200g",
            notes: "1/4 inch cubes, cold",
          },
          {
            item: "Fresh jalapeños",
            amount: "2-3",
            notes: "Diced, seeds optional for heat",
          },
          { item: "Garlic powder", amount: "5g", notes: "Optional" },
        ],
      },
      hydration: "70%",
      timeline: [
        {
          step: "Feed Starter",
          duration: "4-12 hours",
          notes: "Until doubled",
        },
        {
          step: "Mix Dough",
          duration: "15 minutes",
          notes: "Without inclusions",
        },
        { step: "Autolyse", duration: "30 minutes", notes: "Rest dough" },
        { step: "Add Salt", duration: "5 minutes", notes: "Mix thoroughly" },
        {
          step: "Bulk Fermentation",
          duration: "3-4 hours",
          notes: "3 sets of stretch and folds",
        },
        {
          step: "Add Inclusions",
          duration: "10 minutes",
          notes: "Fold in cheese and jalapeños",
        },
        {
          step: "Final Folds",
          duration: "1 hour",
          notes: "1-2 more sets of folds",
        },
        {
          step: "Shape",
          duration: "15 minutes",
          notes: "Gentle to keep inclusions intact",
        },
        { step: "Cold Proof", duration: "8-12 hours", notes: "Refrigerate" },
        {
          step: "Bake",
          duration: "45 minutes",
          notes: "450°F, watch cheese browning",
        },
      ],
    },
    {
      name: "Chocolate Cherry Sourdough",
      source: "Original Recipe",
      description:
        "A delightful sweet sourdough with dark chocolate and dried cherries.",
      ingredients: {
        dough: [
          {
            item: "Ripe sourdough starter",
            amount: "150g",
            notes: "100% hydration",
          },
          { item: "Water, lukewarm", amount: "300g", notes: "90-95°F" },
          { item: "Bread flour", amount: "400g", notes: "" },
          { item: "Whole wheat flour", amount: "100g", notes: "For nuttiness" },
          { item: "Salt", amount: "10g", notes: "2 teaspoons" },
          { item: "Honey", amount: "30g", notes: "For subtle sweetness" },
        ],
        inclusions: [
          { item: "Dark chocolate", amount: "150g", notes: "Roughly chopped" },
          {
            item: "Dried cherries",
            amount: "150g",
            notes: "Soaked in warm water",
          },
          {
            item: "Cocoa powder",
            amount: "20g",
            notes: "For color and flavor",
          },
        ],
      },
      hydration: "72%",
      timeline: [
        { step: "Soak Cherries", duration: "1 hour", notes: "In warm water" },
        { step: "Feed Starter", duration: "4-12 hours", notes: "Until active" },
        {
          step: "Mix Dough",
          duration: "15 minutes",
          notes: "Include cocoa powder",
        },
        { step: "Autolyse", duration: "30 minutes", notes: "Rest dough" },
        {
          step: "Add Inclusions",
          duration: "10 minutes",
          notes: "Fold in chocolate and drained cherries",
        },
        {
          step: "Bulk Fermentation",
          duration: "4-5 hours",
          notes: "4 sets of stretch and folds",
        },
        {
          step: "Shape",
          duration: "15 minutes",
          notes: "Careful not to tear with chocolate",
        },
        { step: "Cold Proof", duration: "8-12 hours", notes: "Refrigerate" },
        {
          step: "Bake",
          duration: "40 minutes",
          notes: "425°F, watch chocolate burning",
        },
      ],
    },
    {
      name: "Walnut Raisin Cinnamon",
      source: "Original Recipe",
      description:
        "A warmly spiced sourdough with toasted walnuts and plump raisins.",
      ingredients: {
        dough: [
          {
            item: "Ripe sourdough starter",
            amount: "200g",
            notes: "100% hydration",
          },
          { item: "Water, lukewarm", amount: "300g", notes: "90-95°F" },
          { item: "Bread flour", amount: "450g", notes: "" },
          { item: "Whole wheat flour", amount: "50g", notes: "For texture" },
          { item: "Salt", amount: "10g", notes: "2 teaspoons" },
          { item: "Honey", amount: "30g", notes: "Optional" },
        ],
        inclusions: [
          { item: "Walnuts", amount: "150g", notes: "Toasted and chopped" },
          { item: "Raisins", amount: "150g", notes: "Soaked in warm water" },
          { item: "Ground cinnamon", amount: "10g", notes: "Or to taste" },
        ],
      },
      hydration: "70%",
      timeline: [
        {
          step: "Toast Walnuts",
          duration: "10 minutes",
          notes: "Cool completely",
        },
        { step: "Soak Raisins", duration: "1 hour", notes: "In warm water" },
        { step: "Feed Starter", duration: "4-12 hours", notes: "Until active" },
        {
          step: "Mix Dough",
          duration: "15 minutes",
          notes: "Include cinnamon",
        },
        { step: "Autolyse", duration: "30 minutes", notes: "Rest dough" },
        {
          step: "Add Inclusions",
          duration: "10 minutes",
          notes: "Fold in nuts and drained raisins",
        },
        {
          step: "Bulk Fermentation",
          duration: "4-5 hours",
          notes: "4 sets of stretch and folds",
        },
        { step: "Shape", duration: "15 minutes", notes: "Gentle handling" },
        { step: "Cold Proof", duration: "12-15 hours", notes: "Refrigerate" },
        { step: "Bake", duration: "45 minutes", notes: "425°F" },
      ],
    },
    {
      name: "Roasted Garlic & Herb",
      source: "Original Recipe",
      description: "A savory loaf packed with roasted garlic and fresh herbs.",
      ingredients: {
        dough: [
          {
            item: "Ripe sourdough starter",
            amount: "200g",
            notes: "100% hydration",
          },
          { item: "Water, lukewarm", amount: "320g", notes: "90-95°F" },
          { item: "Bread flour", amount: "500g", notes: "" },
          { item: "Salt", amount: "10g", notes: "2 teaspoons" },
        ],
        inclusions: [
          {
            item: "Whole garlic heads",
            amount: "2-3",
            notes: "Roasted until soft",
          },
          { item: "Fresh rosemary", amount: "2 tbsp", notes: "Finely chopped" },
          { item: "Fresh thyme", amount: "2 tbsp", notes: "Leaves only" },
          { item: "Olive oil", amount: "30g", notes: "From roasted garlic" },
        ],
      },
      hydration: "74%",
      timeline: [
        {
          step: "Roast Garlic",
          duration: "45 minutes",
          notes: "400°F until soft",
        },
        { step: "Feed Starter", duration: "4-12 hours", notes: "Until active" },
        { step: "Mix Dough", duration: "15 minutes", notes: "Basic dough" },
        { step: "Autolyse", duration: "30 minutes", notes: "Rest dough" },
        {
          step: "Add Inclusions",
          duration: "10 minutes",
          notes: "Fold in garlic, herbs, and oil",
        },
        {
          step: "Bulk Fermentation",
          duration: "4-5 hours",
          notes: "4 sets of stretch and folds",
        },
        { step: "Shape", duration: "15 minutes", notes: "Gentle handling" },
        { step: "Cold Proof", duration: "8-12 hours", notes: "Refrigerate" },
        { step: "Bake", duration: "45 minutes", notes: "450°F with steam" },
      ],
    },
  ];

  const filteredRecipes = recipes.filter(
    (recipe) =>
      recipe.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipe.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="mb-4">
      <h2 className="mb-4">Sourdough Recipes</h2>
      <InputGroup className="mb-4">
        <Form.Control
          placeholder="Search recipes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </InputGroup>

      <Accordion>
        {filteredRecipes.map((recipe, index) => (
          <Accordion.Item key={index} eventKey={index.toString()}>
            <Accordion.Header>
              <div>
                <h5 className="mb-0">{recipe.name}</h5>
                <small className="text-muted">{recipe.description}</small>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">Recipe Details</h6>
                    </Card.Header>
                    <Card.Body>
                      <p>
                        <strong>Source:</strong> {recipe.source}
                      </p>
                      {recipe.sourceUrl && (
                        <p>
                          <strong>Original Recipe:</strong>{" "}
                          <a
                            href={recipe.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View here
                          </a>
                        </p>
                      )}
                      <p>
                        <strong>Hydration:</strong> {recipe.hydration}
                      </p>
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Ingredients</h6>
                    </Card.Header>
                    <Card.Body>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Ingredient</th>
                            <th>Amount</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recipe.ingredients.dough.map((ingredient, i) => (
                            <tr key={i}>
                              <td>{ingredient.item}</td>
                              <td>{ingredient.amount}</td>
                              <td>{ingredient.notes}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Timeline</h6>
                    </Card.Header>
                    <Card.Body>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Step</th>
                            <th>Duration</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recipe.timeline.map((step, i) => (
                            <tr key={i}>
                              <td>{step.step}</td>
                              <td>{step.duration}</td>
                              <td>{step.notes}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};

export default Recipes;
